import { useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { Sparkles, ChevronRight, BookOpenCheck } from "lucide-react";
import { useSideRail } from "@/state/SideRail";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const tabs: Record<string, { label: string; to: string }> = {
  copilot: { label: "Copilot", to: "/copilot" },
  catalog: { label: "Macro Catalog", to: "/catalog" },
  workflows: { label: "Workflows", to: "/workflows" },
  runs: { label: "Run History", to: "/runs" },
  knowledge: { label: "Knowledge Base", to: "/knowledge" },
  integrations: { label: "CAD Integrations", to: "/integrations" },
  teamcenter: { label: "Teamcenter Bridge", to: "/teamcenter" },
  hosts: { label: "Execution Hosts", to: "/hosts" },
  audit: { label: "Audit Log", to: "/audit" },
  "my-macros": { label: "My Macros", to: "/my-macros" },
  "saved-workflows": { label: "Saved Workflows", to: "/saved-workflows" },
  settings: { label: "Settings", to: "/settings" },
};

const askable = new Set([
  "copilot",
  "catalog",
  "workflows",
  "runs",
  "knowledge",
  "integrations",
  "teamcenter",
  "hosts",
  "audit",
]);

export function TopBar() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { open, toggle } = useSideRail();
  const [guideOpen, setGuideOpen] = useState(false);

  const segments = pathname.split("/").filter(Boolean);
  const tabKey = segments[0] ?? "copilot";
  const tab = tabs[tabKey] ?? tabs.copilot;
  const subSegments = segments.slice(1);
  const showAsk = askable.has(tabKey);

  return (
    <header className="border-sidebar-border bg-sidebar text-sidebar-foreground flex h-11 shrink-0 items-center justify-between border-b px-4">
      <nav className="flex min-w-0 items-center gap-1.5 text-[13px]">
        {subSegments.length === 0 ? (
          <span className="text-white">{tab.label}</span>
        ) : (
          <>
            <Link to={tab.to} className="text-sidebar-muted hover:text-white">
              {tab.label}
            </Link>
            {subSegments.map((s, i) => (
              <span key={i} className="flex min-w-0 items-center gap-1.5">
                <ChevronRight className="text-sidebar-muted/70 h-3 w-3 shrink-0" />
                <span
                  className={`truncate ${
                    i === subSegments.length - 1 ? "text-white" : "text-sidebar-muted"
                  } ${s.length > 20 ? "font-mono text-[12px]" : ""}`}
                  title={decodeURIComponent(s)}
                >
                  {decodeURIComponent(s)}
                </span>
              </span>
            ))}
          </>
        )}
      </nav>
      <div className="flex shrink-0 items-center gap-2">
        {showAsk && (
          <button
            onClick={() => toggle(`About the ${tab.label}`, defaultRailSuggestions(tabKey))}
            className={`inline-flex items-center gap-1.5 rounded-sm border px-2 py-1 text-[12px] transition-colors ${
              open
                ? "border-primary bg-primary/20 text-white"
                : "border-sidebar-border bg-sidebar-hover text-sidebar-foreground hover:bg-sidebar-active hover:text-white"
            }`}
          >
            <Sparkles className="h-3.5 w-3.5" />
            Ask Copilot
          </button>
        )}
        <kbd className="border-sidebar-border bg-sidebar-hover text-sidebar-muted flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]">
          <span className="font-medium">⌘</span>
          <span>K</span>
        </kbd>
        <button
          onClick={() => setGuideOpen(true)}
          className="text-sidebar-muted inline-flex items-center gap-1 text-[11.5px] hover:text-white"
          title="Demo guide"
        >
          <BookOpenCheck className="h-3.5 w-3.5" /> Demo guide
        </button>
        <a href="#" className="text-sidebar-muted text-[12px] hover:text-white">
          Help
        </a>
      </div>
      <Sheet open={guideOpen} onOpenChange={setGuideOpen}>
        <SheetContent side="right" className="w-[440px] sm:max-w-none">
          <SheetHeader>
            <SheetTitle className="text-[14px]">
              Demo guide — five hero flows + morning briefing
            </SheetTitle>
          </SheetHeader>
          <ol className="mt-4 space-y-3 text-[12px]">
            <li className="border-border bg-surface rounded-sm border p-3">
              <div className="text-foreground font-medium">Brief me on today</div>
              <div className="text-muted-foreground mt-1">
                Morning briefing flow — peer reviews, team activity, infrastructure, pattern of the
                day.
              </div>
            </li>
            <li className="border-border bg-surface rounded-sm border p-3">
              <div className="text-foreground font-medium">
                Flow 1 — Body Panel Revision Compare invocation
              </div>
              <div className="text-muted-foreground mt-1">
                NL macro invocation with Teamcenter context resolution. Click the chip{" "}
                <span className="text-foreground font-mono">
                  "Run a Body Panel Revision Compare…"
                </span>
              </div>
            </li>
            <li className="border-border bg-surface rounded-sm border p-3">
              <div className="text-foreground font-medium">
                Flow 2 — Context-aware recommendation
              </div>
              <div className="text-muted-foreground mt-1">
                Click{" "}
                <span className="text-foreground font-mono">
                  "What macros should I run on this B-pillar reinforcement?"
                </span>
              </div>
            </li>
            <li className="border-border bg-surface rounded-sm border p-3">
              <div className="text-foreground font-medium">Flow 3 — Workflow composition</div>
              <div className="text-muted-foreground mt-1">
                Click{" "}
                <span className="text-foreground font-mono">
                  "Build a pre-release workflow for door inner panels."
                </span>{" "}
                Approve in the Action Preview.
              </div>
            </li>
            <li className="border-border bg-surface rounded-sm border p-3">
              <div className="text-foreground font-medium">Flow 4 — Anomaly detection</div>
              <div className="text-muted-foreground mt-1">
                Click{" "}
                <span className="text-foreground font-mono">
                  "Show me anything unusual in this week's macro runs."
                </span>
              </div>
            </li>
            <li className="border-border bg-surface rounded-sm border p-3">
              <div className="text-foreground font-medium">Flow 5 — Pattern mining</div>
              <div className="text-muted-foreground mt-1">
                Click{" "}
                <span className="text-foreground font-mono">
                  "Suggest new macros based on team patterns."
                </span>{" "}
                Promote the Weekly Program Audit workflow.
              </div>
            </li>
          </ol>
          <div className="border-border bg-canvas/40 text-muted-foreground mt-5 rounded-sm border border-dashed p-3 text-[11px]">
            For demonstrator use. Reset session state from{" "}
            <span className="text-foreground">Settings → Demo controls</span> between runs.
          </div>
        </SheetContent>
      </Sheet>
    </header>
  );
}

function defaultRailSuggestions(tabKey: string) {
  switch (tabKey) {
    case "catalog":
      return [
        { id: "rs-c1", text: "Show me the most-executed CATIA macros from the last 30 days" },
        { id: "rs-c2", text: "Which macros have been deprecated this quarter?" },
        { id: "rs-c3", text: "Find macros tagged for B-pillar parts" },
      ];
    case "workflows":
      return [
        { id: "rs-w1", text: "What's the most consistent pre-release workflow in the team?" },
        { id: "rs-w2", text: "Compose a workflow for B-pillar reinforcement pre-release" },
      ];
    case "runs":
      return [
        { id: "rs-r1", text: "Show me failed runs from the last 7 days" },
        { id: "rs-r2", text: "Any anomalies in yesterday's executions?" },
      ];
    case "knowledge":
      return [
        { id: "rs-k1", text: "Find the team's CATScript style guide" },
        { id: "rs-k2", text: "How do I handle modal dialogs in CATIA macros?" },
      ];
    case "integrations":
      return [
        { id: "rs-i1", text: "How does the Adapter Service attach to CATIA?" },
        { id: "rs-i2", text: "What's the license-pool depth right now?" },
      ];
    case "teamcenter":
      return [
        { id: "rs-t1", text: "What SOA operations are required for macro deployment?" },
        { id: "rs-t2", text: "Show me recent Teamcenter dataset writes" },
      ];
    case "hosts":
      return [
        { id: "rs-h1", text: "Which host has the most CNEXT cold-starts this week?" },
        { id: "rs-h2", text: "Why is sailauf-cad-01 offline?" },
      ];
    case "audit":
      return [
        { id: "rs-a1", text: "Show me all macro promotions in the last 30 days" },
        { id: "rs-a2", text: "Which write actions affected Released parts?" },
      ];
    default:
      return [{ id: "s-default", text: "What macros should I run on this part?" }];
  }
}
